"use client";
import { ReactNode, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../icons/Icons";

type FaqItem = {
  question: string;
  answer: string;
};

type FaqProps = {
  items: FaqItem[];
  heading?: string;
  subHeading?: string;
  showBadge?: boolean;
  children?: ReactNode;
};

export default function FAQSection({
  items,
  heading = `Any questions? <strong className="text-(--primary) font-extrabold italic">We got you.</strong>`,
  subHeading = `Everything you need to know about and start your investing journey with MySIPOnline.`,
  children,
}: FaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="flex flex-col 2lg:flex-row gap-10 2lg:gap-100">
      <div className="w-full mx-auto 2lg:max-w-96 text-center 2lg:text-left">
        {children ? (
          <>{children}</>
        ) : (
          <>
            <h2
              className="text-xl xs:text-2xl md:text-3xl font-bold text-black mb-5"
              dangerouslySetInnerHTML={{ __html: heading }}
            />

            <p
              className="text-sm sm:text-base text-[#404040] mb-11"
              dangerouslySetInnerHTML={{ __html: subHeading }}
            />

            <a
              href="https://wa.me/9660032889"
              className="w-full xxs:max-w-72 mx-auto md:ml-0 py-2.5 xl:py-3 rounded-xl md:rounded-2xl border sm:border-2 border-(--primary) bg-white text-(--primary) text-sm xs:text-base md:text-xl font-bold flex items-center justify-center gap-2 transition sm:hover:bg-transparent sm:hover:text-white"
            >
              Contact Support
              <Icon.rightArrow />
            </a>
          </>
        )}
      </div>

      <div className="w-full text-left space-y-4">
        {items.map(({ question, answer }, index) => {
          const isActive = openIndex === index;

          return (
            <div
              key={index}
              className="rounded-xl xs:rounded-3xl 2md:rounded-6xl px-4 sm:px-6 overflow-hidden bg-[#f3f4f7]"
            >
              <div aria-label={question} className="w-full">
                <button
                  onClick={() => handleToggle(index)}
                  className="w-full flex justify-between items-center gap-4 text-left py-2.5 sm:py-5"
                >
                  <span className="text-sm xs:text-base md:text-xl font-bold text-(--primary)">
                    {question}
                  </span>

                  <span
                    className={`w-7 h-7 sm:w-9 sm:h-9 flex justify-center items-center shrink-0 rounded-full ${isActive ? "bg-(--primary)" : "bg-gray-200"}`}
                  >
                    <Icon.dropdownDownArrow
                      className={`transition duration-200 origin-center ${isActive ? "text-white rotate-180" : "text-(--primary) rotate-0"}`}
                    />
                  </span>
                </button>
              </div>

              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden text-left"
                  >
                    <div className="text-[#6A7282] text-xs xs:text-sm md:text-base font-normal pb-3 sm:pb-4 pt-0">
                      <div
                        className="prose faq-content text-[#6A7282] text-xs xs:text-sm md:text-base font-light pb-3 sm:pb-4 pt-0"
                        dangerouslySetInnerHTML={{ __html: answer }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
